import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { ApolloError, gql, useQuery } from "@apollo/client";
import {
  ExceptionReason,
  PageInfo,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { EXCEPTION_REASON_FIELDS } from "../graphql/exceptionReasonFields";

export const PAGE_SIZE = 50;

export const GET_EXCEPTION_REASONS = gql`
  query GetExceptionReasons($courierId: ID!, $after: Int, $limit: Int) {
    exceptionReasonsPaginated(
      courierId: $courierId
      after: $after
      limit: $limit
    ) {
      edges {
        node {
          ...ExceptionReasonFields
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
  ${EXCEPTION_REASON_FIELDS}
`;

const EMPTY_PAGE_INFO: PageInfo = {
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: null,
  endCursor: null,
};

interface ExceptionReasonsCoreDataContextType {
  exceptionReasons: ExceptionReason[];
  pageInfo: PageInfo;
  isLoading: boolean;
  hasError: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const defaultContextValue: ExceptionReasonsCoreDataContextType = {
  exceptionReasons: [],
  pageInfo: EMPTY_PAGE_INFO,
  isLoading: false,
  hasError: false,
  currentPage: 0,
  setCurrentPage: () => {},
};

const ExceptionReasonsCoreDataContext =
  createContext<ExceptionReasonsCoreDataContextType>(defaultContextValue);

export const useExceptionReasonsCoreData = () =>
  useContext(ExceptionReasonsCoreDataContext);

interface ExceptionReasonsCoreDataProviderProps {
  children: ReactNode;
}

export function ExceptionReasonsCoreDataProvider({
  children,
}: ExceptionReasonsCoreDataProviderProps) {
  const { courierId } = useAuth();
  const showError = useErrorToast();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery(GET_EXCEPTION_REASONS, {
    variables: {
      courierId: courierId ?? "",
      after: currentPage * PAGE_SIZE,
      limit: PAGE_SIZE,
    },
    skip: !courierId,
    fetchPolicy: "network-only",
    onError: (err) => showError(err as ApolloError),
  });

  const edges = data?.exceptionReasonsPaginated?.edges ?? [];
  const exceptionReasons: ExceptionReason[] = edges
    .map((edge: { node?: ExceptionReason } | null) => edge?.node)
    .filter(
      (node: ExceptionReason | undefined): node is ExceptionReason => !!node
    );
  const pageInfo: PageInfo =
    data?.exceptionReasonsPaginated?.pageInfo ?? EMPTY_PAGE_INFO;

  // Switching courier while on a later page would otherwise keep querying
  // `after: <stale offset>` against the new courier's list.
  useEffect(() => {
    setCurrentPage(0);
  }, [courierId]);

  // Deleting the last row on a page leaves the user on an empty one. Step back.
  // The `!error` guard is load-bearing: under Apollo's default errorPolicy an
  // error blanks `data`, which is indistinguishable from an empty page, so
  // without it one failed request walks the user to page 0, firing a query and
  // an error toast per step.
  useEffect(() => {
    if (loading || error) return;
    if (exceptionReasons.length > 0) return;
    if (currentPage === 0) return;
    setCurrentPage((page) => page - 1);
  }, [loading, error, exceptionReasons.length, currentPage]);

  const value: ExceptionReasonsCoreDataContextType = {
    exceptionReasons,
    pageInfo,
    isLoading: loading,
    hasError: !!error,
    currentPage,
    setCurrentPage,
  };

  return (
    <ExceptionReasonsCoreDataContext.Provider value={value}>
      {children}
    </ExceptionReasonsCoreDataContext.Provider>
  );
}
